import React from 'react';

const Services = () => {
  const services = [
    {
      id: 1,
      title: "Jewelry Cleaning & Maintenance",
      description: "Professional cleaning and polishing services to restore your jewelry's brilliance.",
      features: ["Ultrasonic cleaning", "Steam polishing", "Inspection for damage", "Complimentary appraisal"],
      price: "From $50",
      duration: "1-2 weeks",
      icon: "❖"
    },
    {
      id: 2,
      title: "Ring Sizing & Repairs",
      description: "Expert resizing and repair services for rings and other jewelry pieces.",
      features: ["Precise sizing", "Prong tightening", "Stone replacement", "Metal repair"],
      price: "From $75",
      duration: "2-4 weeks",
      icon: "✦"
    },
    {
      id: 3,
      title: "Custom Design Consultation",
      description: "Work with our master craftsmen to create your dream piece.",
      features: ["Personal consultation", "3D design rendering", "Material selection", "Timeline planning"],
      price: "Free initial consultation",
      duration: "4-12 weeks",
      icon: "❋"
    },
    {
      id: 4,
      title: "Watch Services",
      description: "Complete maintenance and repair services for luxury timepieces.",
      features: ["Movement servicing", "Crystal replacement", "Band adjustment", "Water resistance testing"],
      price: "From $200",
      duration: "2-6 weeks",
      icon: "❊"
    },
    {
      id: 5,
      title: "Estate Jewelry Evaluation",
      description: "Professional appraisal and authentication of heirloom pieces.",
      features: ["Gemological analysis", "Market value assessment", "Insurance documentation", "Restoration options"],
      price: "From $150",
      duration: "1-3 weeks",
      icon: "❉"
    },
    {
      id: 6,
      title: "Engagement Ring Consultation",
      description: "Personalized guidance for selecting the perfect engagement ring.",
      features: ["Ring style consultation", "Budget planning", "Diamond education", "Customization options"],
      price: "Free",
      duration: "1-2 hours",
      icon: "❈"
    }
  ];

  const testimonials = [
    {
      name: "Mrs. Elizabeth Thornton",
      service: "Custom Design",
      quote: "The craftsmanship on my custom necklace exceeded all expectations. Every detail was perfect.",
      rating: 5
    },
    {
      name: "Dr. Michael Chen",
      service: "Watch Service",
      quote: "My vintage Rolex was restored to pristine condition. The service was impeccable.",
      rating: 5
    },
    {
      name: "Ms. Sophia Rodriguez",
      service: "Ring Sizing",
      quote: "Fast, professional service. My engagement ring fits perfectly now.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="luxury-container py-20">
        <h1 className="text-5xl font-bold text-center mb-4 hero-glow">EXCLUSIVE SERVICES</h1>
        <p className="text-center text-gray-400 mb-16 text-lg tracking-wide hover-glow">
          Expert care and craftsmanship for your precious treasures
        </p>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {services.map((service) => (
            <div key={service.id} className="bg-gray-900/50 rounded-lg p-8 border border-yellow-600/20 hover:border-yellow-500/40 transition-all duration-300 hover-glow">
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold text-yellow-400 mb-3">{service.title}</h3>
              <p className="text-gray-300 mb-4">{service.description}</p>

              <div className="space-y-2 mb-6">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center text-sm text-gray-400">
                    <span className="text-yellow-500 mr-2">•</span>
                    {feature}
                  </div>
                ))}
              </div>

              <div className="border-t border-yellow-600/20 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-yellow-500 font-medium">{service.price}</span>
                  <span className="text-gray-400 text-sm">{service.duration}</span>
                </div>
                <button className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-semibold py-2 rounded transition-colors text-sm">
                  SCHEDULE SERVICE
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Service Guarantee */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-950 to-gray-900 py-12 rounded-lg border border-yellow-600/20 mb-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-yellow-400 mb-4">Our Guarantee</h2>
            <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
              Every piece entrusted to our care receives the utmost attention from our master craftsmen.
              We stand behind our work with a lifetime guarantee on all services.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="text-3xl mb-2">❇</div>
                <h3 className="text-yellow-400 font-semibold mb-2">Master Craftsmen</h3>
                <p className="text-gray-400 text-sm">Over 50 years combined experience</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">❋</div>
                <h3 className="text-yellow-400 font-semibold mb-2">Express Service</h3>
                <p className="text-gray-400 text-sm">Rush services available</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">❖</div>
                <h3 className="text-yellow-400 font-semibold mb-2">Insured & Secure</h3>
                <p className="text-gray-400 text-sm">Fully insured during service</p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Testimonials */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 hover-glow">What Our Clients Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-900/50 p-6 rounded-lg border border-yellow-600/20 hover:border-yellow-500/40 transition-all duration-300 hover-glow">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4 leading-relaxed">"{testimonial.quote}"</p>
                <div>
                  <h4 className="text-yellow-400 font-semibold">{testimonial.name}</h4>
                  <p className="text-gray-500 text-sm">{testimonial.service} Service</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-gradient-to-r from-yellow-600/10 to-transparent py-12 rounded-lg border border-yellow-600/20">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">Ready to Schedule a Service?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Contact our service specialists to discuss your needs and schedule an appointment.
            We're here to ensure your treasures receive the care they deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold px-8 py-3 rounded transition-colors">
              CALL NOW: (212) 555-LUXE
            </button>
            <button className="border border-yellow-600 text-yellow-400 hover:bg-yellow-600 hover:text-black font-semibold px-8 py-3 rounded transition-colors">
              EMAIL US
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;